import {
  type DatadogAuthUserSnapshot,
  type DatadogUserIdentity,
  resolveDatadogUserIdentity,
} from "./datadog-user";

const AUTH_TOKEN_COOKIE = /^(sb-[a-z0-9]+-auth-token)(?:\.(\d+))?$/i;
const BASE64_PREFIX = "base64-";

export function resolveDatadogUserIdentityFromCookieHeader(
  cookieHeader: string
): DatadogUserIdentity | null {
  const session = readSupabaseSessionFromCookieHeader(cookieHeader);
  if (!session) return null;

  const claims = decodeJwtPayload(session.accessToken);
  return resolveDatadogUserIdentity({
    user: asAuthUserSnapshot(session.user),
    claims,
  });
}

function readSupabaseSessionFromCookieHeader(cookieHeader: string): {
  accessToken: string | null;
  user: unknown;
} | null {
  const cookies = parseCookieHeader(cookieHeader);
  const encoded = readEncodedAuthCookie(cookies);
  if (!encoded) return null;

  try {
    const decoded = encoded.startsWith(BASE64_PREFIX)
      ? decodeBase64UrlToString(encoded.slice(BASE64_PREFIX.length))
      : encoded;
    const session = JSON.parse(decoded) as {
      access_token?: unknown;
      user?: unknown;
    };
    const accessToken = typeof session.access_token === "string" ? session.access_token : null;
    if (!accessToken && !session.user) return null;
    return { accessToken, user: session.user };
  } catch {
    return null;
  }
}

function parseCookieHeader(cookieHeader: string): Map<string, string> {
  const cookies = new Map<string, string>();

  for (const part of cookieHeader.split(";")) {
    const separator = part.indexOf("=");
    if (separator === -1) continue;

    const name = part.slice(0, separator).trim();
    if (!name) continue;

    const rawValue = part.slice(separator + 1).trim();
    cookies.set(name, decodeCookieValue(rawValue));
  }

  return cookies;
}

function asAuthUserSnapshot(user: unknown): DatadogAuthUserSnapshot | null {
  if (typeof user !== "object" || user === null) return null;
  return user as DatadogAuthUserSnapshot;
}

function decodeCookieValue(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function readEncodedAuthCookie(cookies: Map<string, string>): string | null {
  const grouped = new Map<string, Map<number, string>>();

  for (const [name, value] of cookies) {
    const match = AUTH_TOKEN_COOKIE.exec(name);
    if (!match) continue;

    const key = match[1];
    if (!key) continue;

    const chunkIndex = match[2] ? Number(match[2]) : -1;
    const chunks = grouped.get(key) ?? new Map<number, string>();
    chunks.set(chunkIndex, value);
    grouped.set(key, chunks);
  }

  for (const chunks of grouped.values()) {
    const unchunked = chunks.get(-1);
    if (unchunked) return unchunked;

    const parts: string[] = [];
    for (let index = 0; chunks.has(index); index += 1) {
      parts.push(chunks.get(index) ?? "");
    }
    if (parts.length > 0) return parts.join("");
  }

  return null;
}

function decodeJwtPayload(token: string | null): unknown {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;

  const payload = parts[1];
  if (!payload) return null;

  try {
    return JSON.parse(decodeBase64UrlToString(payload)) as unknown;
  } catch {
    return null;
  }
}

function decodeBase64UrlToString(value: string): string {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
